import {extraServicesStore, persistentStore} from '@/composables/store-accessor'
import {
    B2B_AUTH_EVENT,
    CHANGE_LOCALE_EVENT,
    CHANGE_PRICE_EVENT,
    EventBus,
    RESET as RESET_EVENT,
    RESET_BOOKING_DATA,
    SEARCH_EVENT,
} from '@/utils/event-bus'
import {
    searchRequest as searchRequestBlank,
    searchResponse as searchResponseBlank,
} from '@/utils/extraServices/extra-services-blank-states'
import {appInstance} from '@/utils/app-accessor'
import {onmessage} from '@/utils/worker-helpers'
import {PRODUCT_NAME} from '@/utils/extraServices/extra-services-const'
import {newSearch as newSearchFn, search as searchFn} from '@/utils/store-helpers'
import {useProductRuntimeStore} from '~/composables/product-runtime-store'
import type {Ref} from 'vue'

export const useExtraServicesRuntimeStore = defineStore('extraServicesRuntime', () => {
    const productRuntimeStore = useProductRuntimeStore()
    const {
        searchActiveCount,
        SET_OFFERS,
        START_SEARCH,
        STOP_SEARCH,
        STOP_FILTER,
        stopSearch,
        filterActive,
        SET_PARTIAL_SEARCH_RESULTS_CTS,
    } = productRuntimeStore

    const city: Ref = ref({})

    function SET_CITY(val: any) {
        city.value = val
    }

    function RESET() {
        searchActiveCount.value = 0
    }

    async function loadCity(id: any) {
        try {
            const {
                cities: [_city],
            } = await appInstance.$api.locations.get({id, limitCities: 1})

            city.value = _city
        } catch (e) {
            city.value = {}
        }
    }

    function clientInit() {
        EventBus.$on(RESET_EVENT, reset)
        EventBus.$on(RESET_BOOKING_DATA, reset)
        EventBus.$on(B2B_AUTH_EVENT, reset)
        EventBus.$on(CHANGE_LOCALE_EVENT, reload)
        EventBus.$on(CHANGE_PRICE_EVENT, changePrice)
        onmessage(PRODUCT_NAME, STOP_FILTER, filterActive, SET_OFFERS)
    }

    function newSearch() {
        newSearchFn(stopSearch, SET_OFFERS, searchRequestBlank(), searchResponseBlank(), extraServicesStore)
    }

    async function changePrice({offerKey, prepareBookResponse}: any) {
        extraServicesStore.REFRESH_BASKET_PRICE({offerKey, prepareBookResponse})
        persistentStore.REFRESH_CONDITIONS({offerKey, prepareBookResponse})
    }

    async function reload() {
        if (city.value.id) await loadCity(city.value.id)
    }

    function reset() {
        newSearchFn(stopSearch, SET_OFFERS, searchRequestBlank(), searchResponseBlank(), extraServicesStore)
        RESET()
        extraServicesStore.RESET()
    }

    async function search(rq: any) {
        START_SEARCH()
        newSearchFn(stopSearch, SET_OFFERS, rq, searchResponseBlank(), extraServicesStore)
        EventBus.$emit(SEARCH_EVENT)
        await searchFn(
            SET_PARTIAL_SEARCH_RESULTS_CTS,
            rq,
            PRODUCT_NAME,
            extraServicesStore,
            appInstance.$api.searchOwnExtraService,
            'products'
        )
        STOP_SEARCH()
    }

    const searchPageLink = computed(() => {
        return (searchRequest: any) => {
            const {partialResponse, convertToCurrency, ...query} = searchRequest
            return {name: 'extraServices', query}
        }
    })

    const infoPageLink = computed(() => {
        return (product: any, searchRequest: any) => {
            const {query} = searchPageLink.value(searchRequest)
            if (product.productId) query.productId = product.productId
            query.name = product.info.name
            return {name: 'extraService', query}
        }
    })

    const searchRequestFromQuery = computed(() => {
        return (query: any) => {
            //TODO Need filter $route.query params
            const rq = Object.assign(searchRequestBlank(), query)
            if (typeof rq.childrenAges === 'string') {
                rq.childrenAges = [parseInt(rq.childrenAges, 10)]
            }
            rq.convertToCurrency = persistentStore.currency
            rq.adults = parseInt(rq.adults, 10)
            rq.countryId = parseInt(rq.countryId, 10)
            rq.cityId = parseInt(rq.cityId, 10)
            if (rq.citizenshipId) {
                rq.citizenshipId = parseInt(rq.citizenshipId, 10)
            }
            delete rq.productId
            return rq
        }
    })

    return {
        ...productRuntimeStore,
        city,
        SET_CITY,
        RESET,
        searchActiveCount,
        loadCity,
        clientInit,
        newSearch,
        changePrice,
        reload,
        reset,
        search,
        searchPageLink,
        infoPageLink,
        searchRequestFromQuery,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useExtraServicesRuntimeStore, import.meta.hot))
